import { z } from "zod";

import { MAX_LENGTH_MESSAGE, MIN_LENGTH_MESSAGE, REQUIRED_MESSAGE } from "@/utils";

import { keepCents } from "../products.money";
import { EProductType } from "../products.type";

export function mapWarrantyMarkupValueFromApi(
  c: Record<string, unknown>,
  baseMarkup?: number,
): number | undefined {
  const rawMarkupMode = c.markup_mode as string | undefined;
  if (rawMarkupMode === "real_currency") {
    return keepCents(c.markup_value) ?? baseMarkup;
  }
  if (c.markup_value !== undefined && c.markup_value !== null) {
    return Number(c.markup_value);
  }
  return baseMarkup;
}

export const stripHtml = (v: string | undefined | null) =>
  (v ?? "").replace(/<[^>]*>/g, " ").trim();

export const warrantyEmissionEnum = z.enum(["apolice", "bilhete"]);

export const warrantyCommercializationRuleEnum = z.enum([
  "age",
  "gender",
  "km",
  "location_state",
  "location_city",
  "vehicle_age",
  "vehicle_type",
  "model_or_brand_groups",
  "custom",
]);

export const warrantyGenderEnum = z.enum(["feminine", "masculine", "lgbt"]);

export const warrantyLocationModeEnum = z.enum(["states", "cities"]);

export const warrantyVehicleAgeRuleEnum = z.enum([
  "age_only",
  "age_and_model",
]);

export const warrantyPremiumPriceEnum = z.enum(["net", "commissioned"]);

export const warrantyMarkupModeEnum = z.enum(["percentage", "real_currency"]);

export const warrantyProLaboreModeEnum = z.enum([
  "real_currency",
  "percentage",
  "custom_rule",
]);

export const warrantyPlanAdjustmentEnum = z.enum([
  "lmi",
  "is",
  "ages",
  "usage_qty_in_term",
  "additional_assistance",
  "custom",
]);

const warrantyPlanAdjustmentSchema = z.object({
  adjustment_type: warrantyPlanAdjustmentEnum,
  new_assistance_value_applies: z.boolean().optional(),
  new_assistance_value: z.coerce.number().optional(),
  new_commission_applies: z.boolean().optional(),
  new_commission_percent: z.coerce.number().optional(),
  additional_assistance_description: z.string().optional(),
});

const optionalPlanPriceNumber = z.preprocess((v: unknown) => {
  if (v === "" || v === null || v === undefined) {
    return undefined;
  }
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) {
    return undefined;
  }
  return n;
}, z.number().optional());

const optionalPlanMaxPrice = z.preprocess((v: unknown) => {
  if (v === "" || v === null || v === undefined) {
    return undefined;
  }
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) {
    return undefined;
  }
  return n;
}, z.number().nullable().optional());

const warrantyPlanSchema = z.object({
  name: z.string().min(1, { message: REQUIRED_MESSAGE }),
  description: z.string().optional(),
  new_coverages: z.string().optional(),
  net_price: optionalPlanPriceNumber,
  minimum_price: optionalPlanPriceNumber,
  maximum_price: optionalPlanMaxPrice,
  sale_price: optionalPlanPriceNumber,
  adjustments: z.array(warrantyPlanAdjustmentSchema).optional(),
});

export function createEmptyWarrantyConfigPlan(): z.infer<typeof warrantyPlanSchema> {
  return {
    name: "",
    description: "",
    new_coverages: "",
    sale_price: undefined,
    adjustments: [],
  };
}

const warrantyProductConfigSchemaBase = z.object({
  emission_type: warrantyEmissionEnum,
  is_collective_policy: z.boolean().optional(),
  contract_active: z.boolean().optional(),
  contract_start_date: z.coerce.date().optional(),
  contract_end_date: z.coerce.date().optional(),
  commercialization_rules: z
    .array(warrantyCommercializationRuleEnum)
    .default([]),
  gender: warrantyGenderEnum.optional(),
  km_min: z.coerce.number().optional(),
  km_max: z.coerce.number().optional(),
  location_mode: warrantyLocationModeEnum.optional(),
  location_text: z.string().optional(),
  vehicle_age_rule: warrantyVehicleAgeRuleEnum.optional(),
  vehicle_age_only_min: z.coerce.number().optional(),
  vehicle_age_and_model_rules: z.string().optional(),
  vehicle_type_rules: z.string().optional(),
  premium_price_type: warrantyPremiumPriceEnum,
  net_price: z.coerce.number().optional(),
  gateway_fee_percent: z.coerce.number().optional(),
  gateway_fee_per_sale: z.coerce.number().optional(),
  minimum_price: optionalPlanPriceNumber,
  maximum_price: optionalPlanMaxPrice,
  sale_price: z.coerce.number().optional(),
  commissioned_price: z.coerce.number().optional(),
  commission_percent: z.coerce.number().optional(),
  markup_enabled: z.boolean().optional().default(false),
  markup_mode: warrantyMarkupModeEnum.optional(),
  markup_value: z.coerce.number().optional(),
  pro_labore_enabled: z.boolean(),
  pro_labore_value_mode: warrantyProLaboreModeEnum.optional(),
  pro_labore_value: z.coerce.number().optional(),
  pro_labore_custom_rule: z.string().optional(),
  has_additional_plans: z.boolean(),
  plans: z.array(warrantyPlanSchema).optional(),
});

const refineWarrantyProductConfig = (cfg: any, ctx: z.RefinementCtx) => {
    if (cfg.emission_type === "apolice") {
      if (cfg.is_collective_policy !== true && cfg.is_collective_policy !== false) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["is_collective_policy"],
        });
      }
    }
    if (cfg.emission_type === "bilhete") {
      if (cfg.contract_active !== true && cfg.contract_active !== false) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["contract_active"],
        });
      }
      if (cfg.contract_active === true) {
        if (!cfg.contract_start_date) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: REQUIRED_MESSAGE,
            path: ["contract_start_date"],
          });
        }
        if (!cfg.contract_end_date) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: REQUIRED_MESSAGE,
            path: ["contract_end_date"],
          });
        }
      }
    }
    if (cfg.commercialization_rules.includes("gender") && !cfg.gender) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REQUIRED_MESSAGE,
        path: ["gender"],
      });
    }
    if (cfg.commercialization_rules.includes("km")) {
      if (cfg.km_min === undefined || cfg.km_max === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["km_min"],
        });
      }
    }
    if (
      cfg.commercialization_rules.includes("location_state") ||
      cfg.commercialization_rules.includes("location_city")
    ) {
      if (!cfg.location_mode) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["location_mode"],
        });
      }
      if (!cfg.location_text?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["location_text"],
        });
      }
    }
    if (cfg.commercialization_rules.includes("vehicle_age")) {
      if (!cfg.vehicle_age_rule) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["vehicle_age_rule"],
        });
      } else if (cfg.vehicle_age_rule === "age_only") {
        if (cfg.vehicle_age_only_min === undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: REQUIRED_MESSAGE,
            path: ["vehicle_age_only_min"],
          });
        }
      } else if (cfg.vehicle_age_rule === "age_and_model") {
        if (!cfg.vehicle_age_and_model_rules?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: REQUIRED_MESSAGE,
            path: ["vehicle_age_and_model_rules"],
          });
        }
      }
    }
    const needVehicleTypeRulesText =
      cfg.commercialization_rules.includes("vehicle_type") ||
      cfg.commercialization_rules.includes("model_or_brand_groups");
    if (needVehicleTypeRulesText && !cfg.vehicle_type_rules?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REQUIRED_MESSAGE,
        path: ["vehicle_type_rules"],
      });
    }
    if (cfg.premium_price_type === "net") {
      if (cfg.net_price === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["net_price"],
        });
      }
    }
    if (cfg.premium_price_type === "commissioned") {
      if (cfg.commissioned_price === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["commissioned_price"],
        });
      }
      if (cfg.commission_percent === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["commission_percent"],
        });
      }
      if (cfg.markup_enabled === true) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Markup não se aplica quando o tipo de preço é comissionado",
          path: ["markup_enabled"],
        });
      }
    }
    if (cfg.pro_labore_enabled) {
      if (!cfg.pro_labore_value_mode) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["pro_labore_value_mode"],
        });
      } else if (cfg.pro_labore_value_mode === "custom_rule") {
        if (!cfg.pro_labore_custom_rule?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: REQUIRED_MESSAGE,
            path: ["pro_labore_custom_rule"],
          });
        }
      } else if (cfg.pro_labore_value === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["pro_labore_value"],
        });
      }
    }
    if (
      cfg.premium_price_type === "net" &&
      cfg.markup_enabled === true
    ) {
      if (!cfg.markup_mode) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["markup_mode"],
        });
      }
      if (
        cfg.markup_value === undefined ||
        cfg.markup_value === null ||
        Number.isNaN(Number(cfg.markup_value))
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["markup_value"],
        });
      }
      if (cfg.markup_mode === "percentage") {
        if (cfg.net_price === undefined || cfg.net_price === null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: REQUIRED_MESSAGE,
            path: ["net_price"],
          });
        }
      }
    }
    // Não exigir plans aqui: permite ir à aba Planos com "possui outros planos" ativo
    // e cadastrar planos depois. A obrigatoriedade fica no superRefine de warrantyInsuranceSchema (submit).
};

export const warrantyProductConfigSchema =
  warrantyProductConfigSchemaBase.superRefine(refineWarrantyProductConfig);

/** Mesmo bloco que Garantia, sem `plans` (planos = faixas em `capital_ranges`). */
export const assistanceProductConfigSchema = warrantyProductConfigSchemaBase
  .omit({ plans: true })
  .extend({
    commercialization_rules: z
      .array(warrantyCommercializationRuleEnum)
      .default([]),
  })
  .superRefine(refineWarrantyProductConfig);

export type TAssistanceProductConfigForm = z.infer<
  typeof assistanceProductConfigSchema
>;

export const warrantyInsuranceSchema = z
  .object({
    type: z.literal(EProductType.WARRANTY_INSURANCE),
    name: z
      .string()
      .min(2, { message: MIN_LENGTH_MESSAGE("2") })
      .max(150, { message: MAX_LENGTH_MESSAGE("150") }),
    insurance_company_id: z.string().uuid({ message: REQUIRED_MESSAGE }),
    payment_gateway_id: z.string().uuid({ message: REQUIRED_MESSAGE }),
    policy_number: z.string().optional().nullable(),
    susep_code: z.string().optional().nullable(),
    iof_value: z
      .preprocess((v) => (v === "" || v === null ? 0 : v), z.coerce.number())
      .default(0),
    type_of_vigency: z.enum(
      [
        "pluriannual",
        "annual",
        "monthly",
        "daily",
        "pay_per_use",
        "lifetime",
      ],
      { message: REQUIRED_MESSAGE },
    ),
    policy_start_date: z.coerce.date().optional(),
    policy_end_date: z.coerce.date().optional(),
    min_permitted_age: z.coerce.number().optional().nullable(),
    max_permitted_age: z.coerce.number().optional().nullable(),
    description: z.string().optional(),
    insurance_coverages: z.string().optional(),
    general_conditions: z.string().optional(),
    excluded_risks: z.string().optional(),
    infinity_sync_as_source: z.boolean().optional(),
    external_product_id: z
      .string()
      .max(255, { message: MAX_LENGTH_MESSAGE("255") })
      .optional()
      .nullable(),
    warranty_product_config: warrantyProductConfigSchema,
  })
  .superRefine((data, ctx) => {
    if (!stripHtml(data.description)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REQUIRED_MESSAGE,
        path: ["description"],
      });
    }
    if (!stripHtml(data.insurance_coverages)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REQUIRED_MESSAGE,
        path: ["insurance_coverages"],
      });
    }
    if (!stripHtml(data.general_conditions)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REQUIRED_MESSAGE,
        path: ["general_conditions"],
      });
    }
    if (!stripHtml(data.excluded_risks)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REQUIRED_MESSAGE,
        path: ["excluded_risks"],
      });
    }
    if (data.warranty_product_config.commercialization_rules.includes("age")) {
      if (
        data.min_permitted_age === undefined ||
        data.min_permitted_age === null ||
        data.max_permitted_age === undefined ||
        data.max_permitted_age === null
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["min_permitted_age"],
        });
      }
    }
    const w = data.warranty_product_config;
    if (w.emission_type === "apolice" && w.is_collective_policy === true) {
      if (!data.policy_number?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["policy_number"],
        });
      }
      if (!data.policy_start_date || !data.policy_end_date) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["policy_start_date"],
        });
      }
    }
    if (w.has_additional_plans) {
      if (!w.plans?.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: REQUIRED_MESSAGE,
          path: ["warranty_product_config", "plans"],
        });
      }
    }
  });

export type TWarrantyInsuranceFormData = z.infer<typeof warrantyInsuranceSchema>;

const defaultWarrantyConfig = (): TWarrantyInsuranceFormData["warranty_product_config"] => ({
  emission_type: "bilhete",
  is_collective_policy: false,
  contract_active: false,
  commercialization_rules: ["age"],
  premium_price_type: "net",
  net_price: 0,
  minimum_price: 0,
  maximum_price: null,
  markup_enabled: false,
  pro_labore_enabled: false,
  has_additional_plans: false,
  plans: [],
});

export function getDefaultAssistanceProductConfig(): TAssistanceProductConfigForm {
  const w = defaultWarrantyConfig();
  const { plans: _omitPlans, ...rest } = w;
  return { ...rest, commercialization_rules: [] } as TAssistanceProductConfigForm;
}

/**
 * API legada: `vehicle_age_rule === "model_or_groups"` passa a ser a regra
 * `model_or_brand_groups` em `commercialization_rules` (sem `vehicle_age`).
 */
export function normalizeLegacyWarrantyCommercialization(
  commercialization_rules: string[],
  vehicle_age_rule: unknown,
): { commercialization_rules: string[]; vehicle_age_rule: unknown } {
  if (vehicle_age_rule !== "model_or_groups") {
    return { commercialization_rules: [...commercialization_rules], vehicle_age_rule };
  }
  let rules = commercialization_rules.filter((r) => r !== "vehicle_age");
  if (!rules.includes("model_or_brand_groups")) {
    rules = [...rules, "model_or_brand_groups"];
  }
  return { commercialization_rules: rules, vehicle_age_rule: undefined };
}

export function getDefaultWarrantyProductForm(): TWarrantyInsuranceFormData {
  return {
    type: EProductType.WARRANTY_INSURANCE,
    name: "",
    payment_gateway_id: "",
    insurance_company_id: "",
    policy_number: "",
    susep_code: null,
    iof_value: 0,
    type_of_vigency: "annual",
    policy_start_date: undefined,
    policy_end_date: undefined,
    min_permitted_age: 0,
    max_permitted_age: 120,
    description: "",
    insurance_coverages: "",
    general_conditions: "",
    excluded_risks: "",
    infinity_sync_as_source: true,
    external_product_id: "",
    warranty_product_config: defaultWarrantyConfig(),
  };
}

export function mergeWarrantyProductConfigFromApi(
  raw: unknown,
): TWarrantyInsuranceFormData["warranty_product_config"] {
  const base = defaultWarrantyConfig();
  if (!raw || typeof raw !== "object") {
    return base;
  }
  const c = raw as Record<string, unknown>;
  const toDate = (v: unknown) =>
    v === null || v === undefined || v === ""
      ? undefined
      : new Date(String(v));

  const plans = Array.isArray(c.plans)
    ? (c.plans as Array<Record<string, unknown>>).map((p) => ({
        ...p,
        net_price: keepCents(p.net_price),
        minimum_price: keepCents(p.minimum_price),
        maximum_price:
          p.maximum_price === null || p.maximum_price === undefined
            ? p.maximum_price ?? null
            : keepCents(p.maximum_price),
        sale_price: keepCents(p.sale_price),
        adjustments: Array.isArray(p.adjustments)
          ? (p.adjustments as Array<Record<string, unknown>>).map((a) => ({
              ...a,
              new_assistance_value: keepCents(a.new_assistance_value),
            }))
          : [],
      }))
    : base.plans;

  const rawRules = Array.isArray(c.commercialization_rules)
    ? [...(c.commercialization_rules as string[])]
    : [...base.commercialization_rules];
  const norm = normalizeLegacyWarrantyCommercialization(
    rawRules,
    c.vehicle_age_rule,
  );

  const markupValueFromApi = mapWarrantyMarkupValueFromApi(c, base.markup_value);

  return {
    ...base,
    ...c,
    emission_type: (c.emission_type as typeof base.emission_type) ?? base.emission_type,
    commercialization_rules:
      norm.commercialization_rules as typeof base.commercialization_rules,
    vehicle_age_rule: norm.vehicle_age_rule as typeof base.vehicle_age_rule,
    contract_start_date: toDate(c.contract_start_date),
    contract_end_date: toDate(c.contract_end_date),
    net_price: keepCents(c.net_price) ?? base.net_price,
    gateway_fee_percent:
      c.gateway_fee_percent === undefined || c.gateway_fee_percent === null
        ? undefined
        : Number(c.gateway_fee_percent),
    gateway_fee_per_sale: keepCents(c.gateway_fee_per_sale),
    minimum_price: keepCents(c.minimum_price) ?? base.minimum_price,
    maximum_price:
      c.maximum_price === null || c.maximum_price === undefined
        ? c.maximum_price ?? null
        : keepCents(c.maximum_price),
    sale_price: keepCents(c.sale_price),
    commissioned_price:
      keepCents(c.commissioned_price) ?? base.commissioned_price,
    pro_labore_value: keepCents(c.pro_labore_value) ?? base.pro_labore_value,
    markup_enabled: Boolean(c.markup_enabled ?? base.markup_enabled),
    markup_mode: (c.markup_mode as typeof base.markup_mode) ?? base.markup_mode,
    markup_value: markupValueFromApi,
    plans,
  };
}

export function mergeAssistanceProductConfigFromApi(
  raw: unknown,
): TAssistanceProductConfigForm {
  const merged = mergeWarrantyProductConfigFromApi(
    raw && typeof raw === "object"
      ? { ...(raw as Record<string, unknown>), plans: [] }
      : { plans: [] },
  );
  const { plans: _omitPlans, ...out } = merged;
  return out as TAssistanceProductConfigForm;
}
